<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useConfigStore } from "@core/stores/config.store"

// ================================
// TYPES & INTERFACES
// ================================
type LogoVariant = "full" | "fullWhite"

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /**
         * Variante du logo : full (couleur) ou fullWhite (blanc).
         */
        variant?: LogoVariant
        /**
         * Classe CSS appliquée à l'image.
         */
        imgClass?: string
        /**
         * Texte alternatif pour l'image (sinon dérivé du scope).
         */
        alt?: string
        width?: string
        height?: string
    }>(),
    {
        variant: "full",
        imgClass: "",
        alt: "",
        width: "auto",
        height: "auto",
    }
)

// ================================
// COMPOSABLES
// ================================
const configStore = useConfigStore()

// ================================
// COMPUTED
// ================================
/** URL du logo selon la variante (full ou full_white). */
const getLogoUrl = computed(() => {
    return props.variant === "fullWhite"
        ? configStore.getScopeLogoFullWhite
        : configStore.getScopeLogoFull
})

/** Texte alternatif : prop alt ou brand/name du scope. */
const getAlt = computed(
    () =>
        props.alt ??
        configStore.getScopeBrand ??
        configStore.getScopeName ??
        "Logo"
)
</script>

<template>
    <!-- ================================ -->
    <!-- LOGO SCOPE -->
    <!-- ================================ -->
    <img
        v-if="getLogoUrl"
        :src="getLogoUrl"
        :alt="getAlt"
        :width="width"
        :height="height"
        class="base-scope-logo object-contain object-left"
        :class="imgClass"
    />
</template>

<style scoped>
/* ================================ */
/* COMPOSANT PRINCIPAL */
/* ================================ */
.base-scope-logo {
    display: block;
}
</style>
